import type { AnyAction, Reducer, Store } from '@reduxjs/toolkit';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { createWrapper, HYDRATE } from 'next-redux-wrapper';

import { groupMembersSlice } from '../../../graphdebate/src/components/groups/group-members-slice';
import { frontConfig } from '../config';
import { tagsSlice } from '../thread/Category/tags-slice';
import { threadsSlice } from '../thread/ThreadMessage/thread-slice';
import { topicSlice } from '../thread/ThreadMessage/topic-slice';
import { topicsSlice } from '../thread/ThreadMessage/topics-slice';
import { logoutActionType } from './redux.utils';
import { chatSlice } from './slices/chat-slice';
import { glSlice } from './slices/graphlogin-slice';
import { utilsSlice } from './slices/utils-slice';
import { yakliSlice } from './slices/yakli-slice';
import { setStore } from './storeIsolated';

const reducersObj = {
  [chatSlice.name]: chatSlice.reducer,
  [groupMembersSlice.name]: groupMembersSlice.reducer,
  [utilsSlice.name]: utilsSlice.reducer,
  [threadsSlice.name]: threadsSlice.reducer,
  [yakliSlice.name]: yakliSlice.reducer,
  [topicsSlice.name]: topicsSlice.reducer,
  [topicSlice.name]: topicSlice.reducer,
  [tagsSlice.name]: tagsSlice.reducer,
  [glSlice.name]: glSlice.reducer,
};
const combinedReducer = combineReducers(reducersObj);

type GetStateFromReducers<Reducers extends typeof reducersObj> = {
  [K in keyof Reducers]: Reducers[K] extends Reducer<infer State> ? State : never;
};

export type RootState = GetStateFromReducers<typeof reducersObj>;

const appReducer = (state: RootState, action: AnyAction) => {
  if (action.type === HYDRATE) {
    // console.log('Delta state applied:');
    // console.log(action.payload);
    return {
      ...state, // use previous state
      ...action.payload, // apply delta from hydration
    };
  }
  if (action.type === logoutActionType) {
    // Below is an example if, on logout, we want to clear groupMembers, but preserve other states:
    // const { groupMembers, ...others } = state;
    // (state as any) = others;

    // Currently, everything is cleared on logout:
    (state as any) = undefined;
  }
  return combinedReducer(state, action);
};

export const makeStore = (): Store<RootState> => {
  const store = configureStore({
    reducer: appReducer as any, // Weird typing error that seems introduced by redux itself.
    devTools: !!frontConfig.IS_DEV,
  });
  setStore(store);
  return store;
};

export type AppStore = ReturnType<typeof makeStore>;
export type AppDispatch = AppStore['dispatch'];

// To use in _app.tsx if we need to retrieve SSR redux store and merge in the client.
// Beware, it's trickier than it seems to be. In the case when the user navigates to a new page (client-side navigation), nextjs may retrieve the target page props, which may result in next-redux-wrapper emitting a new HYDRATE redux action, that should be properly merged. The simple root-level state merge will likely erase previous states.
// A safer approach is to implement the HYDRATE in each slice and review each page SSR that adds content to the redux state.
// Or make a json diff and merge.
export const reduxWrapper = createWrapper<AppStore>(makeStore);
